import { useState, useEffect, useRef, useCallback } from 'react'

export const useTimer = (initialTime: number = 0) => {
  const [duration, setDuration] = useState(initialTime)
  const [isRunning, setIsRunning] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Start a countdown timer that decrements every second until 0
  const startTimer = useCallback(() => {
    if (isRunning || duration <= 0) return
    setIsRunning(true)
    intervalRef.current = setInterval(() => {
      setDuration(prev => {
        const next = Math.max(prev - 1, 0)
        if (next === 0) {
          // Auto stop when reaching zero
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          setIsRunning(false)
        }
        return next
      })
    }, 1000)
  }, [isRunning, duration])

  const stopTimer = useCallback(() => {
    if (isRunning) {
      setIsRunning(false)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isRunning])

  const resetTimer = useCallback(() => {
    setDuration(initialTime)
    stopTimer()
  }, [initialTime, stopTimer])

  const pauseTimer = useCallback(() => {
    stopTimer()
  }, [stopTimer])

  const resumeTimer = useCallback(() => {
    startTimer()
  }, [startTimer])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    duration,
    isRunning,
    startTimer,
    stopTimer,
    resetTimer,
    pauseTimer,
    resumeTimer
  }
}
